import { Injectable } from '@angular/core';
import { SortType, SortDirection } from '../constants-types/Enums';
import { JobStub } from '../core-data-model/Job/JobStub.model';
import { Job } from '../core-data-model/Job.model';
import { Person } from '../core-data-model/Person.model';
import { SearchResult } from '../core-data-model/SearchResult.model';
import { TranslationService } from '../translations';

export type SortingFunction<T> = (a: T, b: T) => number;

@Injectable({ providedIn: 'root' })
export class SortUtilities {
    constructor(private TR: TranslationService) {}

    makeSearchResultSort(type: SortType) {
        switch (type) {
            case SortType.Name:
                // This code duplicated in NewTableLInk
                return this.makeAlphabeticSort((item: SearchResult) => {
                    if (item instanceof Person) {
                        return item.fullName(true);
                    } else if (item instanceof JobStub) {
                        return item.title;
                    }

                    if (item instanceof Person) {
                        return item.fullName();
                    }
                });
            case SortType.AssociatedJob:
                return this.makeAlphabeticSort((item: SearchResult) =>
                    item instanceof Person && item.job
                        ? item.job.title || ''
                        : ''
                );
            case SortType.LastUpdated:
                return this.makeDateSort(
                    (item: Person | Job) => item.lastUpdated
                );
            case SortType.PersonType:
                return this.makeNumberSort((item: Person | Job) => {
                    if (item instanceof Person) {
                        return <number>item.type;
                    } else if (item instanceof Job) {
                        return 0;
                    }
                });
            case SortType.ReferenceProfile:
                return this.makeAlphabeticSort((item: Person | Job) => {
                    if (item instanceof Person) {
                        return item.referencePattern
                            ? this.TR.key(item.referencePattern.key)
                            : '';
                    } else if (item instanceof Job) {
                        return '';
                    }
                });
        }
    }

    makeAlphabeticSort<T>(getProperty: (x: T) => string): SortingFunction<T> {
        return (x, y) => {
            const nameX = getProperty(x).toUpperCase();
            const nameY = getProperty(y).toUpperCase();
            if (nameX < nameY) {
                return -1;
            }
            if (nameX > nameY) {
                return 1;
            }

            // names must be equal
            return 0;
        };
    }

    makeDateSort<T>(getProperty: (x: T) => Date): SortingFunction<T> {
        return (x, y) => {
            const nameX = getProperty(x);
            const nameY = getProperty(y);
            if (nameX < nameY) {
                return -1;
            }
            if (nameX > nameY) {
                return 1;
            }

            // names must be equal
            return 0;
        };
    }

    makeNumberSort<T>(getProperty: (x: T) => number): SortingFunction<T> {
        return (x, y) => {
            const nameX = getProperty(x);
            const nameY = getProperty(y);
            if (nameX < nameY) {
                return -1;
            }
            if (nameX > nameY) {
                return 1;
            }

            // names must be equal
            return 0;
        };
    }

    makeCompositeSort<T>(fns: SortingFunction<T>[]): SortingFunction<T> {
        if (fns.length < 1) {
            return (_x, _y) => 0;
        }
        const functionToUse = fns.shift();
        const furtherSort = this.makeCompositeSort(fns);
        return (x, y) => {
            const result = functionToUse(x, y);
            if (result === 0) {
                return furtherSort(x, y);
            }
            return result;
        };
    }

    adjustSortDirection<T>(
        direction: SortDirection,
        sort: SortingFunction<T>
    ): SortingFunction<T> {
        const dir = direction === SortDirection.Ascending ? 1 : -1;
        return (a: T, b: T) => sort(a, b) * dir;
    }
}
